import { useEffect, useRef } from "react";
import { io } from "socket.io-client";

/**
 * Live WhatsApp events for one practice.
 *
 * The backend already emits to a `practice:<practiceNumber>` room and clients
 * already join every practice on their JWT at connect time
 * (server.js:285-289), so this only has to listen — no room management, no
 * server-side work.
 *
 * Connection settings mirror BootNav.jsx, including the Azure Web PubSub path,
 * so both behave the same way in every environment.
 */

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002";

const SOCKET_URL =
  process.env.REACT_APP_PUBSUB_PROD_URL ||
  process.env.REACT_APP_PUBSUB_URL ||
  URL;

const SOCKET_PATH =
  process.env.REACT_APP_PUBSUB_PROD_URL || process.env.REACT_APP_PUBSUB_URL
    ? `/clients/socketio/hubs/${process.env.REACT_APP_PUBSUB_HUB || "Hub"}`
    : "/socket.io";

export function useWhatsAppSocket({ enabled, handlers }) {
  // Held in a ref so a re-render with new callbacks does not tear down and
  // rebuild the socket — that would drop messages during the reconnect.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return undefined;

    const token =
      sessionStorage.getItem("userToken") || sessionStorage.getItem("user");
    if (!token) return undefined;

    const socket = io(SOCKET_URL, {
      path: SOCKET_PATH,
      auth: { token },
      transports: ["websocket", "polling"],
      forceNew: true,
    });

    const forward = (event) => (payload) =>
      handlersRef.current?.[event]?.(payload);

    const events = [
      "whatsapp:message",
      "whatsapp:status",
      "whatsapp:read",
      "whatsapp:message-updated",
    ];

    for (const event of events) socket.on(event, forward(event));

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [enabled]);
}

export default useWhatsAppSocket;
